import { ErrorBoundary } from '@sentry/react';
import { Button, ButtonIcon, Menu, Flyout, Stacked, useRect, Tooltip } from '@statsbomb/kitbag-components';
import { FilterSetType } from '@statsbomb/parachute-types';
import { useAtomValue, useSetAtom } from 'jotai';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router-dom';
import { filtersToPersistAtom } from '@/atoms/filters/persistedFilters';
import { clearFiltersAtom } from '@/atoms/filters/resetFilters';
import { isLoadFilterSetModalOpenAtom, unwrappedSelectedFilterSetAtom } from '@/atoms/filters/userFilters';
import { filterSetsAtom } from '@/atoms/queries/userConfigs';
import { userConfigActionAtom, userConfigTypeAtom } from '@/atoms/userConfigs';
import { MenuItemWithIcon } from '@/components/menu/MenuItemWithIcon';
import { MoreOptionsMenu } from '@/components/menu/MoreOptionsMenu/MoreOptionsMenu';
import { playerDataLockerFiltersToClear } from '@/consts/filters/playerDataLocker';
import { SYSTEM_WIDE_USER_ID } from '@/consts/userConfigs';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { useDuplicateUserSavedFilters } from '@/hooks/userFilters/useDuplicateUserSavedFilters';
import { useUpsertFilterSetConfig } from '@/hooks/useUpsertFilterSetConfig';
import { UserConfigActions, UserConfigFilterDefinition } from '@/types/userConfigs';

interface MenuItemProps {
  selectedFilterSet: UserConfigFilterDefinition;
  onClick: () => void;
}

const SaveMenuItem = ({ selectedFilterSet, onClick }: MenuItemProps) => {
  const { t } = useTranslation(['general', 'filters']);
  const filtersToPersist = useAtomValue(filtersToPersistAtom);
  const upsertFilterSetConfig = useUpsertFilterSetConfig();
  const disabled = selectedFilterSet.hudlUserId === SYSTEM_WIDE_USER_ID;

  const menuItemComponent = (
    <MenuItemWithIcon
      onClick={() => {
        upsertFilterSetConfig(
          selectedFilterSet.definition.titleSettings.title,
          filtersToPersist,
          selectedFilterSet.configId,
        );
        onClick();
      }}
      icon="Save"
      text={t('save', { ns: 'general' })}
      disabled={disabled}
    />
  );

  return disabled ? (
    <Tooltip delay={200} content={<div className="max-w-64">{t('saveDisabled', { ns: 'filters' })}</div>}>
      {menuItemComponent}
    </Tooltip>
  ) : (
    menuItemComponent
  );
};

const SaveAsMenuItem = ({ selectedFilterSet, onClick }: MenuItemProps) => {
  const { t } = useTranslation('general');
  const saveAsCopy = useDuplicateUserSavedFilters(selectedFilterSet.definition.titleSettings.title);

  return (
    <MenuItemWithIcon
      onClick={() => {
        saveAsCopy();
        onClick();
      }}
      icon="SaveAs"
      text={t('saveAsCopy', { ns: 'general' })}
    />
  );
};

const SaveFiltersButtonWithMenu = ({
  selectedFilterSet,
  isLoading,
}: {
  selectedFilterSet: UserConfigFilterDefinition;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation('general');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isTouch = useIsTouchDevice();
  const [relativeTriggerRef, rect] = useRect<HTMLDivElement>(0);

  const closeMenu = () => setIsMenuOpen(false);

  /* 
    this is to close the menu when the browser is resized
    unfortunately KC doesn't handle this 
  */
  useEffect(closeMenu, [rect]);

  return (
    <div ref={relativeTriggerRef} data-testid={`menu-save-button${isLoading ? '-loading' : ''}`}>
      <ButtonIcon
        variant="secondary"
        displayText="left"
        icon="ChevronDown"
        onClick={() => setIsMenuOpen(true)}
        size="small"
        disabled={isLoading}
      >
        {t('save')}
      </ButtonIcon>
      {isMenuOpen && (
        <Flyout
          testId="user-filters-menu"
          triggerPosition={rect}
          placement="bottom-start"
          /* 125 moves the contents 125px to the left for mobile
          rather than coming off the side */
          offset={{ left: isTouch ? 125 : 0, top: 0 }}
        >
          <Menu onOutsideClick={closeMenu}>
            <SaveMenuItem selectedFilterSet={selectedFilterSet} onClick={closeMenu} />
            <SaveAsMenuItem selectedFilterSet={selectedFilterSet} onClick={closeMenu} />
          </Menu>
        </Flyout>
      )}
    </div>
  );
};

const UserFiltersButtonsBase = ({
  selectedFilterSet,
  isLoading,
  isError,
  hasFilterSets,
}: {
  selectedFilterSet: UserConfigFilterDefinition | null;
  isLoading?: boolean;
  isError?: boolean;
  hasFilterSets?: boolean;
}) => {
  const { t } = useTranslation(['general', 'filters']);
  const isPlayerDataLocker = !!useMatch('/:locale/data-locker/players/*');
  const clearFilters = useSetAtom(clearFiltersAtom);

  const setIsLoadModalOpen = useSetAtom(isLoadFilterSetModalOpenAtom);
  const setUserConfigActions = useSetAtom(userConfigActionAtom);
  const setUserConfigTypeToAmend = useSetAtom(userConfigTypeAtom);

  const isSuspended = isLoading || isError;

  return (
    <Stacked
      direction="row"
      gap="small"
      alignItems="center"
      testId={`user-filters-buttons${isSuspended ? `-suspended` : ''}`}
    >
      <Button
        onClick={() => setIsLoadModalOpen(true)}
        disabled={isLoading || isError || !hasFilterSets}
        size="small"
        variant="secondary"
      >
        {t('load')}
      </Button>
      {!selectedFilterSet ? (
        <Button
          variant="secondary"
          onClick={() => {
            setUserConfigTypeToAmend(FilterSetType.FILTERS);
            setUserConfigActions(UserConfigActions.CREATE);
          }}
          size="small"
          disabled={isLoading || isError}
        >
          {t('save')}
        </Button>
      ) : (
        <SaveFiltersButtonWithMenu selectedFilterSet={selectedFilterSet} isLoading={isLoading} />
      )}
      {isPlayerDataLocker && (
        <MoreOptionsMenu>
          <MenuItemWithIcon
            icon="Erase"
            text={t('clearAllFilters', { ns: 'filters' })}
            onClick={() => clearFilters(playerDataLockerFiltersToClear)}
          />
        </MoreOptionsMenu>
      )}
    </Stacked>
  );
};

const UserFiltersButtonsWithData = () => {
  const filterSets = useAtomValue(filterSetsAtom);
  const selectedFilterSet = useAtomValue(unwrappedSelectedFilterSetAtom);

  return <UserFiltersButtonsBase selectedFilterSet={selectedFilterSet} hasFilterSets={filterSets.length > 0} />;
};

export const UserFiltersButtons = () => (
  <ErrorBoundary fallback={<UserFiltersButtonsBase selectedFilterSet={null} isError />}>
    <Suspense fallback={<UserFiltersButtonsBase selectedFilterSet={null} isLoading />}>
      <UserFiltersButtonsWithData />
    </Suspense>
  </ErrorBoundary>
);
